/**
 * Question list component for displaying DDQ questions.
 * Renders all questions in a scrollable container.
 */

import { useRef, useImperativeHandle, forwardRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import type { DdqSessionQuestion } from '@/types/api.types';
import { QuestionCard } from './QuestionCard';

export interface VirtualizedQuestionListProps {
  questions: DdqSessionQuestion[];
  expandedQuestionId: string | null;
  selectedQuestionIds: Set<string>;
  editingQuestionId: string | null;
  editingResponse: string;
  onToggleExpand: (questionId: string | null) => void;
  onToggleSelect: (questionId: string) => void;
  onAccept: (questionId: string) => void;
  onReject: (questionId: string) => void;
  onStartEdit: (questionId: string, response: string) => void;
  onSaveEdit: (questionId: string) => void;
  onCancelEdit: () => void;
  onUpdateEditingResponse: (response: string) => void;
}

export interface VirtualizedQuestionListHandle {
  scrollToTop: () => void;
  scrollToIndex: (index: number) => void;
}

export const VirtualizedQuestionList = forwardRef<
  VirtualizedQuestionListHandle,
  VirtualizedQuestionListProps
>(function VirtualizedQuestionList(
  {
    questions,
    expandedQuestionId,
    selectedQuestionIds,
    editingQuestionId,
    editingResponse,
    onToggleExpand,
    onToggleSelect,
    onAccept,
    onReject,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onUpdateEditingResponse,
  },
  ref
) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Expose scroll methods for keyboard navigation
  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      parentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    },
    scrollToIndex: (index: number) => {
      const element = parentRef.current?.querySelector(`[data-index="${index}"]`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  }));

  if (questions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">
            No questions match your current filters.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      ref={parentRef}
      className="h-[calc(100vh-400px)] min-h-[400px] overflow-auto"
    >
      {questions.map((question, index) => (
        <div key={question.question_id} data-index={index} className="pb-4">
          <QuestionCard
            question={question}
            isExpanded={expandedQuestionId === question.question_id}
            isSelected={selectedQuestionIds.has(question.question_id)}
            isEditing={editingQuestionId === question.question_id}
            editingResponse={editingResponse}
            onToggleExpand={() =>
              onToggleExpand(
                expandedQuestionId === question.question_id
                  ? null
                  : question.question_id
              )
            }
            onToggleSelect={() => onToggleSelect(question.question_id)}
            onAccept={() => onAccept(question.question_id)}
            onReject={() => onReject(question.question_id)}
            onStartEdit={() =>
              onStartEdit(question.question_id, question.suggested_response)
            }
            onSaveEdit={() => onSaveEdit(question.question_id)}
            onCancelEdit={onCancelEdit}
            onUpdateEditingResponse={onUpdateEditingResponse}
          />
        </div>
      ))}
    </div>
  );
});
